import { useState, useEffect } from 'react'
import instituteRequest from '../../../utils/instituteRequest'

// Custom hook to fetch TPO approval configuration

export const useTpoConfig = (instituteCampusId, studentFormData, form = null) => {
  const [tpoConfig, setTpoConfig] = useState(null)
  const [frozenFields, setFrozenFields] = useState({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTpoConfig = async () => {
      try {
        if (instituteCampusId) {
          const response = await instituteRequest.get(
            `/institutes/${instituteCampusId}/tpo-approval-config`
          )
          if (response?.data?.data) {
            setTpoConfig(response.data.data)
          }
        }
      } catch (error) {
        console.error('Error fetching TPO config:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchTpoConfig()
  }, [instituteCampusId])

  useEffect(() => {
    if (!tpoConfig || !tpoConfig.tpoApprovalConfig || !tpoConfig.tpoApprovalConfig.fields || !tpoConfig.isRequestForTPOApproval) {
      setFrozenFields({})
      return
    }

    const frozen = {}
    const fields = tpoConfig.tpoApprovalConfig.fields
    const admin = studentFormData?.admin || {}
    const personalProfile = studentFormData?.studentPersonalProfile || {}
    const student = studentFormData?.student || {}
    const currentCourse = studentFormData?.currentCourse || {}
    const education = studentFormData?.education || []
    const resume = studentFormData?.resume || {}

    const personalFieldMappings = {
      'firstName': { source: admin, key: 'firstName' },
      'lastName': { source: admin, key: 'lastName' },
      'dob': { source: admin, key: 'dob' },
      'gender': { source: personalProfile, key: 'gender' },
      'primaryEmail': { source: admin, key: 'email' },
      'phoneNumber': { source: admin, key: 'phoneNumber' },
      'permAddrLine1': { source: personalProfile, key: 'permAddrLine1' },
      'permAddrLine2': { source: personalProfile, key: 'permAddrLine2' },
      'permCountry': { source: personalProfile, key: 'permCountry' },
      'permState': { source: personalProfile, key: 'permState' },
      'permCity': { source: personalProfile, key: 'permCity' },
      'permPostCode': { source: personalProfile, key: 'permPostCode' },
      'corrAddrLine1': { source: personalProfile, key: 'corrAddrLine1' },
      'corrAddrLine2': { source: personalProfile, key: 'corrAddrLine2' },
      'corrCountry': { source: personalProfile, key: 'corrCountry' },
      'corrState': { source: personalProfile, key: 'corrState' },
      'corrCity': { source: personalProfile, key: 'corrCity' },
      'corrPostCode': { source: personalProfile, key: 'corrPostCode' },
      'cv': { source: student, key: 'cvUrl' }
    }

    const educationFieldMappings = {
      'tenth.board': { source: education, key: 'board', level: 'tenth' },
      'tenth.endYear': { source: education, key: 'endedOn', level: 'tenth' },
      'tenth.marks': { source: education, key: 'marks', level: 'tenth' },
      'tenth.markSheetUrl': { source: education, key: 'markSheetUrl', level: 'tenth' },
      'twelfth.board': { source: education, key: 'board', level: 'twelfth' },
      'twelfth.endYear': { source: education, key: 'endedOn', level: 'twelfth' },
      'twelfth.marks': { source: education, key: 'marks', level: 'twelfth' },
      'twelfth.markSheetUrl': { source: education, key: 'markSheetUrl', level: 'twelfth' },
      'otherEducation.state': { source: education, key: 'state', level: 'other' },
      'otherEducation.city': { source: education, key: 'city', level: 'other' },
      'otherEducation.collegeName': { source: education, key: 'collegeName', level: 'other' },
      'otherEducation.university': { source: education, key: 'university', level: 'other' },
      'otherEducation.degree': { source: education, key: 'degree', level: 'other' },
      'otherEducation.department': { source: education, key: 'department', level: 'other' },
      'otherEducation.specialisation': { source: education, key: 'specialisation', level: 'other' },
      'otherEducation.endYear': { source: education, key: 'endedOn', level: 'other' },
      'otherEducation.marks': { source: education, key: 'marks', level: 'other' },
      'otherEducation.markSheetUrl': { source: education, key: 'markSheetUrl', level: 'other' },
      'currentCourse.state': { source: currentCourse, key: 'state' },
      'currentCourse.city': { source: currentCourse, key: 'city' },
      'currentCourse.collegeName': { source: currentCourse, key: 'collegeName' },
      'currentCourse.university': { source: currentCourse, key: 'university' },
      'currentCourse.degree': { source: currentCourse, key: 'degree' },
      'currentCourse.department': { source: currentCourse, key: 'department' },
      'currentCourse.specialisation': { source: currentCourse, key: 'specialisation' },
      'currentCourse.endYear': { source: currentCourse, key: 'endedOn' },
      'currentCourse.marks': { source: currentCourse, key: 'marks' },
      'currentCourse.markSheetUrl': { source: currentCourse, key: 'markSheetUrl' },
      'currentCourse.noOfArrears': { source: currentCourse, key: 'noOfArrears' }
    }

    const projectFieldMappings = {
      'projects.title': { source: resume, key: 'projects', arrayField: 'title' },
      'projects.startYear': { source: resume, key: 'projects', arrayField: 'started_in' },
      'projects.endYear': { source: resume, key: 'projects', arrayField: 'ended_in' },
      'projects.description': { source: resume, key: 'projects', arrayField: 'description' },
      'projects.certifications': { source: resume, key: 'projects', arrayField: 'certificate' },
      'projects.addNew': { source: resume, key: 'projects', arrayField: 'addNew' }
    }

    const internshipFieldMappings = {
      'internships.companyName': { source: resume, key: 'internships', arrayField: 'organization' },
      'internships.startYear': { source: resume, key: 'internships', arrayField: 'started_in' },
      'internships.endYear': { source: resume, key: 'internships', arrayField: 'ended_in' },
      'internships.function': { source: resume, key: 'internships', arrayField: 'function' },
      'internships.role': { source: resume, key: 'internships', arrayField: 'role' },
      'internships.industry': { source: resume, key: 'internships', arrayField: 'industry' },
      'internships.description': { source: resume, key: 'internships', arrayField: 'description' },
      'internships.certifications': { source: resume, key: 'internships', arrayField: 'certificate' },
      'internships.addNew': { source: resume, key: 'internships', arrayField: 'addNew' }
    }

    const workExperienceFieldMappings = {
      'workExperience.companyName': { source: resume, key: 'workExperience', arrayField: 'organization' },
      'workExperience.startYear': { source: resume, key: 'workExperience', arrayField: 'started_in' },
      'workExperience.endYear': { source: resume, key: 'workExperience', arrayField: 'ended_in' },
      'workExperience.function': { source: resume, key: 'workExperience', arrayField: 'function' },
      'workExperience.role': { source: resume, key: 'workExperience', arrayField: 'role' },
      'workExperience.industry': { source: resume, key: 'workExperience', arrayField: 'industry' },
      'workExperience.description': { source: resume, key: 'workExperience', arrayField: 'description' },
      'workExperience.certifications': { source: resume, key: 'workExperience', arrayField: 'certificate' },
      'workExperience.addNew': { source: resume, key: 'workExperience', arrayField: 'addNew' }
    }

    const certificationFieldMappings = {
      'certifications.name': { source: resume, key: 'courses', arrayField: 'title' },
      'certifications.issueYear': { source: resume, key: 'courses', arrayField: 'started_in' },
      'certifications.expiryYear': { source: resume, key: 'courses', arrayField: 'ended_in' },
      'certifications.description': { source: resume, key: 'courses', arrayField: 'description' },
      'certifications.credentialId': { source: resume, key: 'courses', arrayField: 'certificate' },
      'certifications.addNew': { source: resume, key: 'courses', arrayField: 'addNew' },
      'courses.certifications': { source: resume, key: 'courses', arrayField: 'certificate' }
    }

    const checkAndFreezeFields = (fieldMappings, sectionPrefix = '') => {
      Object.entries(fieldMappings).forEach(([configKey, config]) => {
        const fieldConfig = fields[configKey]
        
        if (fieldConfig && (fieldConfig.freezeAfterVerification || fieldConfig.freezeAfterApproval)) {
          let fieldValue
          
          if (config.level) {
            const levelData = Array.isArray(config.source) 
              ? config.source.find(item => item.educationLevel === config.level)
              : null
            fieldValue = levelData ? levelData[config.key] : null
          } else if (config.arrayField) {
            const arrayData = config.source[config.key] || []
            fieldValue = arrayData.length > 0 ? arrayData[0][config.arrayField] : null
          } else {
            fieldValue = config.source[config.key]
            
            // Special handling for currentCourse.marks - use averageMarks if marks is not available
            // Don't use averageMarks if it's 0 or "0" (0 marks should not be frozen)
            if (configKey === 'currentCourse.marks' && !fieldValue && config.source.averageMarks && parseFloat(config.source.averageMarks) !== 0) {
              fieldValue = config.source.averageMarks
            }
            
            if (form && !fieldValue && (configKey.includes('Country') || configKey.includes('State') || configKey.includes('City'))) {
              fieldValue = form.getFieldValue?.(configKey)
            }
          }
          
          // Special handling for noOfArrears - treat 0 as no value (no arrears = no freeze)
          let hasValue
          if (configKey === 'currentCourse.noOfArrears') {
            hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '' && fieldValue !== 0 &&
                       (!Array.isArray(fieldValue) || fieldValue.length > 0)
          } else {
            hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '' && fieldValue !== 0 &&
                       (!Array.isArray(fieldValue) || fieldValue.length > 0)
          }
          
          if (hasValue) {
            const fieldNameParts = configKey.split('.')
            const fieldName = sectionPrefix 
              ? `${sectionPrefix}.${fieldNameParts[fieldNameParts.length - 1]}` 
              : (fieldNameParts.length > 1 ? fieldNameParts[1] : configKey)
            frozen[fieldName] = true
            
            if (fieldName && fieldName.includes('Country') && !frozen[fieldName + 'Id']) {
              frozen[fieldName + 'Id'] = true
            }
            if (fieldName && fieldName.includes('State') && !frozen[fieldName + 'Id']) {
              frozen[fieldName + 'Id'] = true
            }
            if (fieldName && fieldName.includes('City') && !frozen[fieldName + 'Id']) {
              frozen[fieldName + 'Id'] = true
            }
          }
        }
      })
    }

    checkAndFreezeFields(personalFieldMappings)
    checkAndFreezeFields(educationFieldMappings)
    checkAndFreezeFields(projectFieldMappings, 'projects')
    checkAndFreezeFields(internshipFieldMappings, 'internships')
    checkAndFreezeFields(workExperienceFieldMappings, 'workExperience')
    checkAndFreezeFields(certificationFieldMappings, 'certifications')

    setFrozenFields(frozen)
  }, [tpoConfig, studentFormData])

  return { tpoConfig, frozenFields, loading }
}
